import { formatTimer, PrimaryButton, ReactIcons, Spacer, TopBar, useThemeContext } from "@kalyzee/kalyzee-ui";
import React, { useMemo } from "react";
import { UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import { getNormalUser } from "../../helpers/user";

import styles from "./BaseTopBar.module.css";
import { UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { User } from "@kalyzee/kast-web-sdk/websocket";
import { RecordIndicator } from "../utils/RecordIndicator";
import { MainClockRenderer } from "../renderers/mainClock";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";

export interface BaseTopBarProps {
  user?: User | UserOutputDTO;
  meetingName?: string;
  meetingStartedAt?: string;
  showDate?: boolean;
  showLogo?: boolean;
  watchingNumber?: number;
  settingsBtnRef?: React.Ref<HTMLDivElement>;
  meetingTimerRef?: React.Ref<HTMLDivElement>;
  onBack?: () => void;
  onSettings?: () => void;
  onMeetingTimerClick?: () => void;
}

const _BaseTopBar: React.FC<BaseTopBarProps> = ({
  user,
  meetingName,
  meetingStartedAt,
  showDate,
  showLogo,
  watchingNumber,
  settingsBtnRef,
  meetingTimerRef,
  onBack,
  onSettings,
  onMeetingTimerClick,
}) => {
  const themeCtx = useThemeContext();
  const isMobile = useAppStoreSelector(state => state.isMobile);

  const meetingStartedAtDate = useMemo(() => {
    return meetingStartedAt ? new Date(meetingStartedAt) : undefined;
  }, [meetingStartedAt]);

  const left = useMemo(() => {
    const items: React.ReactNode[] = [];
    // Optional : Back
    if (onBack) {
      items.push(
        <PrimaryButton
          className={styles.backArrow}
          onClick={onBack}
        >
          <ReactIcons.ArrowBack />
        </PrimaryButton>
       );
    }

    // Optional : Settings
    if (onSettings) {
      if (items.length) items.push(<Spacer />);
      items.push(
        <div ref={settingsBtnRef}>
          <PrimaryButton
            className={styles.settings}
            onClick={onSettings}
          >
            <ReactIcons.Settings />
          </PrimaryButton> 
        </div>
      );
    }

    // Optional : User
    if (user) {
      const { displayName, avatar } = getNormalUser(user);
      if (items.length) items.push(<Spacer />);
      items.push(
        <span className={styles.userAvatar}>
          <UserAvatar
            style={{ width: 26, height: 26 }}
            mode="image"
            image={avatar}
            username={displayName}
          />
        </span>
      );
      items.push(<Spacer />);
      items.push(<span className={styles.userName}>{displayName}</span>);
    }

    return items;
  }, [user, onBack, themeCtx.theme, themeCtx.setPreferredTheme]);

  const right = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (meetingName || meetingStartedAtDate || watchingNumber !== undefined) {
      items.push(
        <div className={styles.meetingData}>
          {meetingName && (
            // Optional : Meeting name
            <div className={styles.meetingName}>
              {meetingName}
            </div>
          )}
          {meetingStartedAtDate && (
            <div ref={meetingTimerRef}>
              <PrimaryButton
                className={styles.meetingMetrics}
                onClick={onMeetingTimerClick}
              >
                <RecordIndicator className={styles.recordIndicator} />
                <div className={styles.elapsedTime}>
                  <MainClockRenderer
                    render={_ => formatTimer(meetingStartedAtDate)}
                  />
                </div>
              </PrimaryButton>
            </div>
          )}
          {watchingNumber !== undefined && (
            <div className={styles.watchers}>
              <ReactIcons.Eye style={{ width: "1em", height: "1em" }} />
              <span>{watchingNumber}</span>
            </div>
          )}
        </div>
      );
    }

    // Optional : Date
    if (showDate) {
      if (items.length) items.push(<Spacer />);
      items.push(
        <MainClockRenderer
          render={date => {
            let dateString = date.toLocaleDateString(undefined, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            });
            dateString = dateString.charAt(0).toUpperCase() + dateString.slice(1);
            let timeString = date.toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            });
            return (
              <span className={styles.date}>{dateString} - {timeString}</span>
            );
          }}
        />
      );
    }

    return items;
  }, [meetingName, meetingStartedAtDate, showDate, watchingNumber, isMobile, onMeetingTimerClick]);

  const center = useMemo(() => {
    const items: React.ReactNode[] = [];
    // Optional : Logo
    if (showLogo) {
      items.push(
        <ReactIcons.LogoFullAnimated style={{ width: 85, height: 24, marginLeft: "1em", marginRight: "1em" }} />,
      );
    }
    return items;
  }, [showLogo]);

  return (
    <TopBar
      left={left}
      right={right}
      center={center}
      className={styles.container}
    />
  );
}

export const BaseTopBar = React.memo(_BaseTopBar);